// frontend/src/components/Shared/VoiceRecorder.jsx
import React, { useState, useRef } from 'react';
import socket from '../../services/socket';
import { useAudioDevices } from '../../hooks/useAudioDevices';
import AudioDevicePicker from './AudioDevicePicker';
import { buildAudioPipeline, captureMic, captureSystemAudio } from '../../hooks/useAudioCapture';

// Audio source modes
const SOURCES = [
  {
    id: 'mic',
    icon: '🎤',
    label: 'Microphone',
    hint: 'Captures your voice or any selected mic / virtual device',
  },
  {
    id: 'system',
    icon: '🖥️',
    label: 'Tab / System Audio',
    hint: 'Captures YouTube, Zoom, Meet, or any browser tab — you\'ll pick the tab in the Chrome prompt',
  },
];

const VoiceRecorder = ({ roomKey, sessionKey, isRecording, setIsRecording }) => {
  const [audioSource, setAudioSource] = useState('mic'); // 'mic' | 'system'
  const [error, setError]             = useState('');

  const isRecordingRef = useRef(false);
  const pipelineRef    = useRef(null); // { stop }

  const {
    devices, deviceId, selectDevice,
    getAudioConstraints, refreshAfterPermission,
  } = useAudioDevices();

  // ── Start ────────────────────────────────────────────────────────────────────
  const startRecording = async () => {
    try {
      setError('');
      isRecordingRef.current = true;

      let stream;

      if (audioSource === 'system') {
        stream = await captureSystemAudio();
        // When the user closes the share dialog / stops sharing, auto-stop
        stream.getAudioTracks()[0].onended = () => {
          if (isRecordingRef.current) stopRecording();
        };
      } else {
        const constraints = getAudioConstraints();
        stream = await captureMic(constraints);
        refreshAfterPermission();
      }

      // Wire up PCM pipeline → socket
      pipelineRef.current = buildAudioPipeline(stream, (pcmBuf) => {
        if (isRecordingRef.current) {
          socket.emit('audio-data', pcmBuf);
        }
      });

      socket.emit('start-transcription', { roomKey, sessionKey });
      setIsRecording(true);
    } catch (err) {
      isRecordingRef.current = false;
      console.error('Audio capture error:', err.name, err.message);

      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        if (audioSource === 'system') {
          setError('Screen sharing cancelled. Click "Start Recording" and select a tab in the Chrome prompt, then tick "Share tab audio".');
        } else {
          setError('Microphone permission denied. Please allow access in browser settings.');
        }
      } else if (err.name === 'OverconstrainedError') {
        setError('Selected mic device is unavailable. Switching to system default...');
        selectDevice('');
      } else if (err.message?.includes('No audio track')) {
        setError('No audio captured. In the Chrome picker, select a tab and make sure "Share tab audio" is checked (bottom-left).');
      } else {
        setError(`Audio error: ${err.message}`);
      }
    }
  };

  // ── Stop ─────────────────────────────────────────────────────────────────────
  const stopRecording = () => {
    isRecordingRef.current = false;
    pipelineRef.current?.stop();
    pipelineRef.current = null;
    socket.emit('stop-transcription', { roomKey });
    setIsRecording(false);
  };

  const isSystem      = audioSource === 'system';
  const activeMicLabel = !isSystem && devices.find(d => d.deviceId === deviceId)
    ?.label?.split('(')[0].trim();

  return (
    <div className="mt-4 space-y-3">

      {/* ── Error banner ── */}
      {error && (
        <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-lg">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* ── Audio source tabs (only when not recording) ── */}
      {!isRecording && (
        <div>
          <p className="text-xs font-medium text-gray-600 mb-1.5">Audio Source</p>
          <div className="grid grid-cols-2 gap-2">
            {SOURCES.map(s => (
              <button
                key={s.id}
                onClick={() => { setAudioSource(s.id); setError(''); }}
                className={`flex flex-col items-start p-3 rounded-xl border-2 text-left transition-all
                  ${audioSource === s.id
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 bg-white hover:border-gray-300'
                  }`}
              >
                <span className="text-xl mb-1">{s.icon}</span>
                <span className={`text-xs font-semibold ${audioSource === s.id ? 'text-blue-700' : 'text-gray-700'}`}>
                  {s.label}
                </span>
                <span className="text-xs text-gray-400 mt-0.5 leading-tight">{s.hint}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ── Mic device picker (only for mic source, not recording) ── */}
      {!isRecording && !isSystem && (
        <AudioDevicePicker
          devices={devices}
          deviceId={deviceId}
          onSelect={selectDevice}
          disabled={false}
        />
      )}

      {/* ── System audio tip (only for system source, not recording) ── */}
      {!isRecording && isSystem && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3.5 text-xs text-amber-800 space-y-1.5">
          <p className="font-semibold text-amber-900">📋 How to capture YouTube / interview audio:</p>
          <ol className="list-decimal list-inside space-y-1">
            <li>Click <strong>Start Recording</strong> below</li>
            <li>Chrome shows a screen-share picker — click the <strong>Chrome Tab</strong> tab</li>
            <li>Select the <strong>YouTube</strong> (or meeting) tab</li>
            <li>Tick <strong>"Share tab audio"</strong> at the bottom-left ✔</li>
            <li>Click <strong>Share</strong> — transcription starts automatically</li>
          </ol>
          <p className="text-amber-600 mt-1">
            💡 Play the video after clicking Share. The AI will answer each question it hears.
          </p>
        </div>
      )}

      {/* ── Record / Stop button ── */}
      <div>
        {!isRecording ? (
          <button onClick={startRecording}
            className={`w-full flex items-center justify-center space-x-2 py-3 px-6 font-medium rounded-lg transition-all shadow-lg text-white
              ${isSystem
                ? 'bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700'
                : 'bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700'
              }`}>
            <span className="text-lg">{isSystem ? '🖥️' : '🎤'}</span>
            <span>{isSystem ? 'Share Tab Audio & Start' : 'Start Recording'}</span>
          </button>
        ) : (
          <button onClick={stopRecording}
            className="w-full flex items-center justify-center space-x-2 py-3 px-6 bg-gradient-to-r from-red-500 to-red-600 text-white font-medium rounded-lg hover:from-red-600 hover:to-red-700 transition-all shadow-lg">
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M9 10a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z" />
            </svg>
            <span>Stop Recording</span>
          </button>
        )}
      </div>

      {/* ── Active indicator ── */}
      {isRecording && (
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <div className="flex space-x-1 items-end">
              {[8, 14, 6, 12, 10, 14, 8].map((h, i) => (
                <div key={i}
                  className={`w-1 rounded animate-pulse ${isSystem ? 'bg-blue-500' : 'bg-green-500'}`}
                  style={{ height: `${h}px`, animationDelay: `${i * 80}ms` }} />
              ))}
            </div>
            <span className="text-sm text-gray-600">
              {isSystem ? 'Capturing tab audio...' : 'Listening...'}
            </span>
          </div>
          {!isSystem && activeMicLabel && (
            <span className="text-xs text-gray-400 truncate max-w-[140px]">{activeMicLabel}</span>
          )}
          {isSystem && (
            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium">
              🖥️ Tab Audio
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default VoiceRecorder;
